import { useEffect, useState } from "react";

import { fetchCounterpartyProfile } from "../lib/api/counterparties.ts";
import { describeApiError } from "../lib/apiError.ts";
import { COUNTERPARTY_COPY } from "./counterparty.constants.ts";
import type { CounterpartyView, ProfileLines } from "./counterparty.model.ts";
import { toProfileLines } from "./counterparty.model.ts";

type Answer =
  | { ondoId: string; kind: "open"; profile: ProfileLines }
  | { ondoId: string; kind: "error"; message: string };

/**
 * Opens and closes the "who you are dealing with" panel — OND-81.
 *
 * `open(ondoId)` is the one intent. The read follows the id; the answer is
 * stored WITH the id it answers, and the view is derived from the two rather
 * than set — so a slow answer for an id that is no longer open is simply not
 * the current id's, and nothing in the effect writes state synchronously. A
 * refusal is shown in the server's own sentence, which for this door is
 * deliberately the same for "no such id" and "not yours to check".
 */
export function useCounterpartyController() {
  const [ondoId, setOndoId] = useState<string | null>(null);
  const [answer, setAnswer] = useState<Answer | null>(null);

  useEffect(() => {
    if (ondoId === null) return;

    let active = true;

    fetchCounterpartyProfile(ondoId)
      .then((response) => {
        if (active) setAnswer({ ondoId, kind: "open", profile: toProfileLines(response) });
      })
      .catch((cause: unknown) => {
        if (active) {
          setAnswer({
            ondoId,
            kind: "error",
            message: describeApiError(cause, COUNTERPARTY_COPY.fallbackError),
          });
        }
      });

    return () => {
      active = false;
    };
  }, [ondoId]);

  const view: CounterpartyView =
    ondoId === null
      ? { kind: "closed" }
      : answer?.ondoId !== ondoId
        ? { kind: "loading", ondoId }
        : answer.kind === "open"
          ? { kind: "open", ondoId, profile: answer.profile }
          : { kind: "error", ondoId, message: answer.message };

  return {
    view,
    open: (id: string) => setOndoId(id),
    close: () => setOndoId(null),
  };
}
